import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Title, Text } from '@siftscience/focus-components/text'
import { useDisputeContext } from '../context/dispute-context'
import { useBuyerIdentity } from '../useDet'
import { titleizeText } from '../utils'
import Loader from '../loader'
import PieChart from './components/pie-chart'

const TRANSACTION_CURRENCY_PRECISION = 1000000

// Helper to format currency values
const formatMicroAmount = (amount, currencyCode) => {
  if (typeof amount !== 'number' || !currencyCode) return ''
  const value = amount / TRANSACTION_CURRENCY_PRECISION
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode
  }).format(value)
}

// Styles from before (no changes needed here)
const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: '8px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    marginRight: 48
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    backgroundColor: '#F7F7F7',
    height: '48px',
    borderBottom: '1px solid #E0E0E0'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    gap: '24px'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  sectionTitle: {
    textAlign: 'center'
  },
  horizontalSeparator: {
    height: '1px',
    width: '100%',
    backgroundColor: '#E0E0E0'
  },
  chartSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    flexWrap: 'wrap'
  },
  chartDescription: {
    maxWidth: '200px',
    textAlign: 'center'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px 24px'
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  }
}))

const Identity = () => {
  const { disputeId } = useDisputeContext()
  const {
    data: identityData,
    isLoading,
    isAllowed,
    error
  } = useBuyerIdentity(disputeId)
  const classes = useStyles()

  const chartData = useMemo(() => {
    if (!identityData) return null

    // Convert BOTH values to dollars before comparing them
    const disputesAmountInDollars =
      (identityData.totalDisputesAmount || 0) / TRANSACTION_CURRENCY_PRECISION

    const totalTransactionsInDollars =
      Object.values(
        identityData.transactionData?.totalAmountsByCurrency || {}
      ).reduce((acc, value) => acc + value, 0) / TRANSACTION_CURRENCY_PRECISION

    const percentage =
      totalTransactionsInDollars > 0
        ? (
            (disputesAmountInDollars / totalTransactionsInDollars) *
            100
          ).toFixed(0)
        : 0

    return {
      data: [
        {
          name: 'Disputes Amount',
          value: disputesAmountInDollars, // Use the dollar value
          color: '#eb3b2e'
        },
        {
          name: 'Good Transactions Amount',
          // Use dollar values for this calculation too
          value: Math.max(
            0,
            totalTransactionsInDollars - disputesAmountInDollars
          ),
          color: '#05a131'
        }
      ],
      name: ['Disputes vs', 'Transactions'],
      description: `Disputes amount is ${percentage}% of all transaction value.`
    }
  }, [identityData])

  if (isLoading) {
    return <Loader />
  }

  if (error) {
    return <Text>There was an error retrieving buyer identity data.</Text>
  }

  if (!identityData) {
    return <Text>No buyer identity data found for this individual.</Text>
  }

  const {
    totalDisputesNumber,
    totalDisputesAmount,
    currencyCode,
    totalWonDisputes,
    totalLostDisputes,
    reasonCodes = {},
    transactionData = {},
    orderData = {}
  } = identityData

  const reasonCodesKeys = Object.keys(reasonCodes)
  const transactionCurrencyKeys = Object.keys(
    transactionData.totalAmountsByCurrency || {}
  )
  const orderCurrencyKeys = Object.keys(orderData.totalAmountsByCurrency || {})

  return (
    <div className={`shadow ${classes.card}`}>
      <div className={classes.header}>
        <Title size="medium">Buyer Identity</Title>
      </div>
      <div className={classes.content}>
        <div className={classes.section}>
          <Title size="medium" className={classes.sectionTitle}>
            General Information
          </Title>
          {chartData && (
            <div className={classes.chartSection}>
              <PieChart
                data={chartData.data}
                width={150}
                height={150}
                chartName={chartData.name}
              />
              <Text size="small" className={classes.chartDescription}>
                {chartData.description}
              </Text>
            </div>
          )}
          <div className={classes.statsGrid}>
            <div className={classes.statItem}>
              <Title size="small">Total Disputes</Title>
              <Text size="small">{totalDisputesNumber}</Text>
            </div>
            <div className={classes.statItem}>
              <Title size="small">Total Disputed Amount</Title>
              <Text size="small">
                {formatMicroAmount(totalDisputesAmount, currencyCode)}
              </Text>
            </div>
            <div className={classes.statItem}>
              <Title size="small">Won Disputes</Title>
              <Text size="small">{totalWonDisputes}</Text>
            </div>
            <div className={classes.statItem}>
              <Title size="small">Lost Disputes</Title>
              <Text size="small">{totalLostDisputes}</Text>
            </div>
          </div>
        </div>

        <div className={classes.horizontalSeparator} />

        {reasonCodesKeys.length > 0 && (
          <div className={classes.section}>
            <Title size="medium" className={classes.sectionTitle}>
              Dispute Reason Codes
            </Title>
            <div className={classes.statsGrid}>
              {reasonCodesKeys.map(code => (
                <div className={classes.statItem} key={code}>
                  <Title size="small">{titleizeText(code)}</Title>
                  <Text size="small">{reasonCodes[code]}</Text>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={classes.horizontalSeparator} />

        <div className={classes.section}>
          <Title size="medium" className={classes.sectionTitle}>
            Lifetime Transactions ({transactionData.transactionCount || 0})
          </Title>
          <div className={classes.statsGrid}>
            {transactionCurrencyKeys.map(currency => (
              <div className={classes.statItem} key={currency}>
                <Title size="small">{currency.toUpperCase()} Total</Title>
                <Text size="small">
                  {formatMicroAmount(
                    transactionData.totalAmountsByCurrency[currency],
                    currency
                  )}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.horizontalSeparator} />

        <div className={classes.section}>
          <Title size="medium" className={classes.sectionTitle}>
            Lifetime Orders ({orderData.orderCount || 0})
          </Title>
          <div className={classes.statsGrid}>
            {orderCurrencyKeys.map(currency => (
              <div className={classes.statItem} key={currency}>
                <Title size="small">{currency.toUpperCase()} Total</Title>
                <Text size="small">
                  {formatMicroAmount(
                    orderData.totalAmountsByCurrency[currency],
                    currency
                  )}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Identity
